/**
 * Date and Time formatting using native Intl.DateTimeFormat() constructor and format method
 * Creates objects that enable language-sensitive date and time formatting
 *
 * A Unicode BCP 47 locale identifier, each separated by hyphens, consists of:
 * - a language code,
 * - (optionally) a script code,
 * - (optionally) a region (or country) code,
 * - (optionally) one or more variant codes, and
 * - (optionally) one or more extension sequences,
 *
 * Reference:
 * (https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/DateTimeFormat/DateTimeFormat)
 * (https://github.com/unicode-org/cldr/tree/master/common)
 * (https://tc39.es/ecma402/#datetimeformat-objects)
 * (https://tc39.es/ecma402/#sec-intl-datetimeformat-constructor)
 * (https://www.unicode.org/reports/tr35/tr35.html#BCP_47_Conformance)
 * (https://www.unicode.org/reports/tr35/#Unicode_locale_identifier)
 *
 */
// DEFAULTS
const defaultLocale = "en-US";
//////////////////////////////////////////////////////////////////////////// UTILITY FUNCTIONS
const addDays = (date, days) => {
    const result = new Date(date);
    return new Date(result.setDate(result.getDate() + +days));
};
const addMonths = (date, months) => {
    const result = new Date(date);
    return new Date(result.setMonth(result.getMonth() + +months));
};
const addYears = (date, years) => {
    const result = new Date(date);
    return new Date(result.setFullYear(result.getFullYear() + +years));
};
// const isSameTime = (date1: Date, date2: Date): boolean => {
//   return (
//     new Date(new Date().setUTCHours(date1.getUTCHours(), date1.getUTCMinutes(), 0, 0)).getTime() ==
//     new Date(new Date().setUTCHours(date2.getUTCHours(), date2.getUTCMinutes(), 0, 0)).getTime()
//   );
// };
/**
 * Evaluate 2 Date objects to determine if Dates are the same date
 * @param date1 Date object
 * @param date2 Date object
 * @returns boolean
 */
const isSameDate = (date1, date2) => {
    return (new Date(Date.UTC(date1.getUTCFullYear(), date1.getUTCMonth(), date1.getUTCDate())).valueOf() ==
        new Date(Date.UTC(date2.getUTCFullYear(), date2.getUTCMonth(), date2.getUTCDate())).valueOf());
};
/**
 * Evaluate 2 Date objects to determine if Dates are the same date & time
 * @param date1 Date object
 * @param date2 Date object
 * @returns boolean
 */
const isSameDateTime = (date1, date2) => {
    return (new Date(Date.UTC(date1.getUTCFullYear(), date1.getUTCMonth(), date1.getUTCDate(), date1.getUTCHours(), date1.getUTCMinutes())).valueOf() ==
        new Date(Date.UTC(date2.getUTCFullYear(), date2.getUTCMonth(), date2.getUTCDate(), date2.getUTCHours(), date2.getUTCMinutes())).valueOf());
};
/**
 * Evaluate 2 Date objects to determine if Dates are in the same month
 * @param date1 Date object
 * @param date2 Date object
 * @returns boolean
 */
const isSameMonth = (date1, date2) => {
    return isSameYear(date1, date2) && date1.getUTCMonth() == date2.getUTCMonth();
};
/**
 * Evaluate 2 Date objects to determine if Dates are in same year
 * @param date1 Date object
 * @param date2 Date object
 * @returns boolean
 */
const isSameYear = (date1, date2) => {
    return date1.getUTCFullYear() === date2.getUTCFullYear();
};
/**
 *
 * @param dates - array of Dates
 * @returns ordered array of Dates
 */
const orderDates = (dates) => {
    return dates.sort((a, b) => a.valueOf() - b.valueOf());
};
/**
 *
 * @param listToCheck - key value data object to check for locale-specific and default
 * @param countryCode - region string
 * @returns string
 */
const hasCountryDefault = (listToCheck, countryCode) => {
    const listKeys = Object.keys(listToCheck);
    const hasCode = countryCode ? listKeys.find((el) => el.includes(countryCode)) : undefined;
    return hasCode ? listToCheck[hasCode] : listToCheck["001"];
};
/**
 * @param loc - locale string
 * @returns formatted locale string or null
 */
function getCleanLocale(loc) {
    return (loc && loc.replace(/[.:].*/, "").replace(/_/g, "-")) || null;
}
/**
 * @param {undefined | string} locale
 * @returns {string} BCP47 locale string
 */
function getLocale(locale) {
    if (Array.isArray(locale)) {
        return getCleanLocale(locale[0]) || defaultLocale;
    }
    return getCleanLocale(locale) || defaultLocale;
}
/**
 * @param {Date | number | undefined} date either Date object or timestamp number
 * @returns {Date} Date object
 */
const getVerifiedDate = (date) => {
    // if not passed, then use today
    if (!date) {
        return new Date();
    }
    // if a number, treat as a timestamp
    if (typeof date === "number") {
        return new Date(new Date(date));
    }
    return new Date(date);
};
/**
 * getLocalDateTimeFromUTC formats a UTC date string into a localized date time.
 * This was made specifically for react native, where UTC string to date conversions
 * aren't handled correctly by default.
 * @param {string} dateString - a UTC date string
 * @returns {Date} - Date object localized to the phone's timezone
 * @deprecated This function is stripping the timezone from the date. If you're relying on the
 * timezone to be stripped, you more likely have a bug upstream or downstream that needs to be
 * better documented.
 */
const getLocalDateTimeFromUTC = (dateString) => {
    const date = new Date(dateString);
    const milliseconds = Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), date.getHours(), date.getMinutes(), date.getSeconds());
    return new Date(milliseconds);
};
/**
 * getReactNativeLocalDateTime is a wrapper for getLocalDateTimeFromUTC
 * @param {string} dateString - a UTC date string
 * @returns {Date} - Date object localized to the phone's timezone
 * @deprecated See getLocalDateTimeFromUTC
 */
const getReactNativeLocalDateTime = (dateString) => getLocalDateTimeFromUTC(dateString);
export { addDays, addMonths, addYears, getLocalDateTimeFromUTC, getLocale, getReactNativeLocalDateTime, getVerifiedDate, hasCountryDefault, isSameDate, isSameDateTime, isSameMonth, isSameYear, orderDates, };
